import * as React from 'react'
import styled from 'styled-components'
import Layout from 'containers/Layout'
import Container from 'theme/atoms/Container'
import Gallery from './Gallery'
import InformationBox from './InformationBox'
import BuyBox from './BuyBox'
import { useInitializer, useDisplayVariant } from 'modules/productDetail'
import theme from 'theme'
import { Crumb } from 'theme/atoms/Breadcrumbs'
import { createCrumbsFromCategoryTree } from 'utils/createCrumbs'
import Seo from 'theme/atoms/SEO'
import ProductSlider from 'theme/molecules/ProductSlider'
import fetchBySku from 'utils/productListFetcher/bySku'
import config from 'config'
import withTranslations from './withTranslations'
import useTranslations from 'hooks/useTranslations'
import pdpLdJson from './pdpLdJson'
import { Helmet } from 'react-helmet'
import FilterDrawer from './FilterDrawer'
import DyProductSlider from 'theme/molecules/DyProductSlider'
import useCart from 'hooks/account/cart/useCart'
import { prefetchProductDetail } from 'modules/productDetail/utils/ssrHydration'
import { Variant } from 'modules/productDetail/types'
import DeliveryByPartnerModal from 'theme/molecules/DeliveryByPartnerModal'
import genProductImageUrl from 'utils/genProductImageUrl'
import use404StaticBlock from 'utils/use404StaticBlock'
import Story from 'theme/atoms/Story'
import getContainerIDBySku from './getContainerIDBySku'
import { usePDPFilterDrawerOpen } from 'modules/ui'
import * as dy from 'utils/dy-hybrid'
import calculateDeliveryDate, {
  DeliveryInformation
} from 'utils/calculateDeliveryDate'
import AlternativContainer from './AlternativContainer/AlternativContainer'

type Props = {
  '*': string
  location: {
    pathname: string
    origin: string
  }
  pageContext: {
    brandPath: string
  }
  serverData: {
    apiResponse?: Variant[]
    sku?: string
    is404?: boolean
  }
}

type ServerContext = {
  params: {
    '*': string
  }
  query: {
    sku: string
  }
}
const MemoLayout = React.memo(Layout)

export function PDP(props: Props) {
  const defaultMatch = props.location.pathname.match(/^.*\/pdp\/([0-9]{6})\/$/)
  const skuonlyMatch = props.location.pathname.match(/^.*sku-([0-9]{8}).*$/)
  const { open: filterDrawerOpen, setOpen: filterDrawerSetOpen } =
    usePDPFilterDrawerOpen()
  const containerID = defaultMatch
    ? defaultMatch[1]
    : skuonlyMatch
      ? skuonlyMatch[1]
      : ''

  const staticBlock = use404StaticBlock()
  React.useEffect(() => {
    if (props.serverData?.is404) {
      dy.reportPageVisit({
        type: 'OTHER',
        data: [],
        location: window.location.href,
        locale: config.i18n.locale.replace('-', '_')
      })
    }
  }, [props.serverData?.is404])

  if (props.serverData?.is404) {
    return (
      <MemoLayout
        pageContext={{
          type: 'Other',
          ready: true
        }}
      >
        <Seo
          canonicalUrl={`${config.baseUrl}/${config.locale}/pdp/${containerID}/`}
          location={props.location}
          title={staticBlock.title}
          description={staticBlock.description}
          type="article"
        />
        <Container>
          <div>
            <div style={{ height: 40 }} />
            <Story story={staticBlock.story} cfId={staticBlock.contentfulID} />
            <DyProductSlider
              dySelector="wdv-2564_404page_fallback"
              title="Recommended Products"
              gridArea="404-dy-slider"
              layoutSize="normal"
            />
          </div>
        </Container>
      </MemoLayout>
    )
  }

  const t = useTranslations<'templates-PDP'>()
  const cart = useCart()
  const filter = React.useMemo(() => ({ containerID }), [containerID])
  useInitializer(filter, props.serverData?.apiResponse, props.serverData?.sku)
  const variant = useDisplayVariant()
  const [showDeliveryByPartnerModal, setShowDeliveryByPartnerModal] =
    React.useState(false)

  React.useEffect(() => {
    window?.scrollTo(0, 0)
  }, [])
  const title = t.asText(false, 'meta_title', {
    productName: variant.data.title.trim(),
    brand: variant.data.brand.trim(),
    attribut: variant.data.subtitle.trim()
  })

  const description = t.asText(false, 'meta_description', {
    productName: variant.data.title.trim(),
    brand: variant.data.brand.trim(),
    attribut: variant.data.subtitle.trim()
  })
  const crumbs: Crumb[] = React.useMemo(() => {
    return createCrumbsFromCategoryTree(
      variant.data.mainCategory || variant.data.categories,
      variant.data.title
    )
  }, [variant.data])

  const pathArray = React.useMemo(() => {
    return Object.values(variant.data.mainCategory)[
      Object.keys(variant.data.mainCategory).length - 1
    ]
  }, [variant.data.mainCategory])
  const pdpLdJsonscript = React.useMemo(() => {
    return pdpLdJson(
      variant.data,
      config.i18n.currency_ISO,
      '',
      cart.data.deliveryCost
    )
  }, [variant.data, cart.data.deliveryCost])
  const isSoltOut = React.useMemo(() => {
    const deliveryInformations: DeliveryInformation = calculateDeliveryDate(
      variant.data,
      1
    )

    return !variant.data.sellable || !deliveryInformations.isBuyable
  }, [variant])

  return (
    <MemoLayout
      key={containerID}
      breadcrumbs={crumbs}
      isFetching={!!variant.data.isDummy}
      pageContext={{
        type: 'Product',
        ready: !variant.isFetching && !variant.data.isDummy,
        sku: variant.data.sku
      }}
    >
      <Seo
        canonicalUrl={`${config.baseUrl}/${config.locale}/pdp/${containerID}/`}
        title={
          !variant.data.isDummy
            ? title
            : 'Gastronomiebedarf | Schnell und einfach online'
        }
        description={
          !variant.data.isDummy
            ? description
            : 'Alles, was Gastgeber brauchen. 40.000 Artikel unter einem Dach. Service & Beratung. Kauf ohne Risiko. Profiqualität, faire Preise.'
        }
        img={
          !variant.data.isDummy
            ? genProductImageUrl(variant.data.images?.imageWeb[0], 's')
            : undefined
        }
        type="article"
        breadcrumbs={crumbs}
        location={props.location}
        product={variant.data}
        hrefLang={props.serverData?.apiResponse?.[0]?.hrefLang || []}
      />

      {!variant.data.isDummy && (
        /* eslint-disable-next-line @kaminrunde/firescout/jsx-expression-needs-state */
        <Helmet>
          <script
            data-react-helmet="true"
            id="pdpLd"
            type="application/ld+json"
          >
            {JSON.stringify(pdpLdJsonscript)}
          </script>
        </Helmet>
      )}

      <Container>
        {isSoltOut && (
          <AlternativContainer
            data-cy-state="show-alternativ-products"
            btnLabel={t('highlight_product_action_btn_label')}
            brandPath={props.pageContext.brandPath}
          />
        )}
        <Wrapper
          data-cy-ctx="templates/PDP"
          data-cy-state={variant.data.isDummy && 'is-dummy'}
        >
          <Gallery path={pathArray?.[0] || ''} />
          <div className="sticky-wrapper">
            <BuyBox
              onFilterDrawerClick={() => {
                filterDrawerSetOpen(true)
              }}
              brandPath={props.pageContext.brandPath}
              path={props.location.pathname}
              onDeliveryInfoClick={() => setShowDeliveryByPartnerModal(true)}
            />
          </div>
          <InformationBox productPath={pathArray?.[0] || ''} />
          <div className="DySliderWrapper">
            <DyProductSlider
              dySelector="pdp-recommendations-slot1"
              title="Recommended Products"
              gridArea="pdp-dy-slider"
              layoutSize="normal"
            />
          </div>
          {variant.data.related?.crossSells.length > 0 && (
            <div
              className={'SliderCrosssell'}
              data-cy-state={'slider-crosssells-shown'}
            >
              <ProductSlider
                key={variant.data.related.crossSells.join('')}
                title={t('productSlider_related_to', {
                  productName: variant.data.title
                })}
                fetchFn={fetchBySku(variant.data.related.crossSells, 20)}
                maxProducts={
                  variant.data.related.crossSells.length < 20
                    ? variant.data.related.crossSells.length
                    : 20
                }
                listname={'pdpCrosssell'}
              />
            </div>
          )}
          {filterDrawerOpen && (
            <FilterDrawer data-cy-state="filterdrawer-visible" />
          )}
          {showDeliveryByPartnerModal && (
            <DeliveryByPartnerModal
              data-cy-state="show-delivery-by-partner-modal"
              onClose={() => setShowDeliveryByPartnerModal(false)}
            />
          )}
        </Wrapper>
      </Container>
    </MemoLayout>
  )
}

export async function getServerData(context: ServerContext) {
  try {
    const skuMatch = context.params['*'].match(/^.*sku-([0-9]{8}).*$/)
    let containerID = ''
    if (skuMatch) {
      const sku = skuMatch[1]
      containerID = (await getContainerIDBySku(sku)) ?? sku
    } else {
      containerID = context.params['*'].split('-').reverse()[0]
    }
    if (!containerID || containerID === '') {
      return {
        status: 301,
        headers: {
          Location: `/search/`
        }
      }
    }
    const { sku } = context.query
    const apiResponse = await prefetchProductDetail({
      containerID,
      size: null,
      style: null,
      color: null,
      variant: null
    })
    if (apiResponse.length === 0) {
      return {
        status: 404,
        props: {
          is404: true
        }
      }
    }
    return {
      status: 200,
      props: {
        apiResponse,
        sku
      }
    }
  } catch (error) {
    return {
      status: 500,
      props: {}
    }
  }
}

export default withTranslations(PDP)

const Wrapper = styled.div`
  display: grid;
  grid:
    'Gallery'
    'BuyBox'
    'InformationBox'
    'DySlider'
    'SliderCrosssell'
    / 1fr;
  grid-column-gap: ${theme.spacing('m')};
  grid-row-gap: ${theme.spacing('ml')};

  @media (min-width: ${theme.ms.MD}px) {
    grid:
      'Gallery        BuyBox'
      'InformationBox BuyBox'
      'DySlider       DySlider'
      'SliderCrosssell SliderCrosssell'
      / 1fr 1fr;
    grid-column-gap: ${theme.spacing('m')};
    grid-row-gap: ${theme.spacing('m')};
  }
  @media (min-width: ${theme.ms.LG}px) {
    grid:
      'Gallery        BuyBox'
      'InformationBox BuyBox'
      'DySlider       DySlider'
      'SliderCrosssell SliderCrosssell'
      / 3fr 2fr;
    grid-column-gap: ${theme.spacing('m')};
    grid-row-gap: ${theme.spacing('m')};
  }

  @media (min-width: ${theme.ms.XL}px) {
    grid:
      'Gallery       BuyBox' auto
      'InformationBox BuyBox'
      'DySlider      DySlider'
      'SliderCrosssell SliderCrosssell'
      /2fr 1fr;
    grid-column-gap: ${theme.spacing('xl')};
    grid-row-gap: ${theme.spacing('l')};
  }

  > .Gallery {
    grid-area: Gallery;
  }

  > .sticky-wrapper {
    height: 100%;
    grid-area: BuyBox;
    > .BuyBox {
      position: sticky;
      top: ${theme.spacing('s')};
    }
  }
  > .DySliderWrapper {
    grid-area: DySlider;
  }
  > .InformationBox {
    grid-area: InformationBox;
  }

  > .SliderCrosssell {
    grid-area: SliderCrosssell;
  }
`
